import { CMSController, ContentPostContentLocationDownloadDto, ControllerHelper, FileEntryDownloadDto, ProjectGroupDownloadDto } from "collaboration-service";
import { FileEntrySearchResultDto } from "collaboration-service/dist/dtos/FileEntrySearchResultDto";
import { ProjectGroupSearch } from "collaboration-service/dist/dtos/ProjectGroupSearch";
import MoreButton, { MoreButtonEntryProps, MoreMenuButton } from "components/MoreButton/MoreButton";
import { Button, Icon, Image, Theme, Tooltip, fadeColor, getColor, getUriFromLinkByName, lightenColor, styled, useThemePart } from "imaginarity-react-ui";
import * as _ from "lodash";
import * as React from "react";
import { useSelector } from "react-redux";
import { ApplicationState, useAppSelector } from "services/ApplicationState/ApplicationState";
import { calcMinsInDaysAndHourAndMins, shallowCompare } from "services/Helpers";
import { nop } from "services/Helpers/EventHelper";
import { hasLink } from "services/Helpers/LinkHelpers";
import { gotoNamedRoute, useCheckWhereIAm } from "services/Helpers/RoutingHelper";
import { getTranslated, getTranslatedStandardDate, shortenBytes } from "services/Helpers/TranslationHelpers";
import { useImgI18N } from "services/ImgI18N";
import useCopyToClipboard from "services/useCopyToClipboard";
import { cmsConfig } from "../CMSConfig";
import CMSFileEntryAppIcon from "../CMSFileEntryAppIcon";
import CMSFileVersionsInfo from "../CMSFileVersionsInfo";
import { cmsTheme } from "../CMSTheme";
import { createCMSSearchString, updateCMSSearchOnRoute, useCMSSearchEmptyFeFilter, useCMSSearchEmptyFilter } from "./useCMSSearchHelpers";
import { useUseCMSSearchSelector } from "./useCMSSearchStateHandling";
import { ThemeContext } from "styled-components";

type LoadedDataType<S, T> = T & { loadedData?: S };

interface RendererProps<T, S extends any = any> {
    data: LoadedDataType<S, T>;
}

const HDiv = styled.div`
    width: 40px;
    height: 40px;
    background-color: ${p => getColor(p, "background")};
    overflow: hidden; 
    padding: 4px;
`;
const FG = styled.div`
    display: grid;
    grid-template-columns: 30px 1fr; 
`;
const FF = styled.div`
    padding-right: 10px;
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;
const FGC = styled.div`
    width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;
const FGCCFN = styled.span`
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;
const LeftAligned = styled.div`
    text-align: left;
`;
const Bolder = styled.span`
    font-weight: bolder;
    padding-right: 5px;
    line-height: 20px;
`;

export const LinkButton = <T extends { originId?: string }>(p: RendererProps<T> & { type: "projects" | "files", copyMsg: string }) => {
    const { data, type, copyMsg } = p;
    const { t } = useImgI18N("cms");
    const searchQuery = createCMSSearchString("", "*", useCMSSearchEmptyFilter, { ...useCMSSearchEmptyFeFilter, originId: data.originId }, undefined, undefined, type, undefined, undefined);
    const loc = window.location;
    const hashEnd = window.location.hash.indexOf("?");
    const newHash = (hashEnd >= 0 ? window.location.hash.substring(0, hashEnd) : window.location.hash) + (searchQuery.length > 0 ? `?${searchQuery}` : "");
    const url = `${loc.protocol}//${loc.hostname}${loc.port ? ":" + loc.port : ""}/index.html${newHash}&open=true`;
    const { copyLinkToClipboard, linkCopied } = useCopyToClipboard(url, 2000);
    const call = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        copyLinkToClipboard();
    }, [copyLinkToClipboard])
    if (!data.originId)
        return <div />;
    return linkCopied ? <Button icon="check" iconColor="@accentGreen" disabled kind="transparentButton" tooltip={{ show: true, tooltipText: t(copyMsg) }} /> : <Button icon="link" onClick={call} kind="transparentButton" tooltip={{ tooltipText: t("copy link to clipboard") }} /> //translateError
}

export const AdminCopyButtons = (p: RendererProps<ProjectGroupSearch, ProjectGroupDownloadDto>) => {
    return <Button icon="copy" iconColor="@accentBlue" />
}

export const AdminMoreButton = (p: RendererProps<ProjectGroupSearch, ProjectGroupDownloadDto>) => {
    const { data } = p;
    const { t } = useImgI18N("cms");
    const onProjectClicked = useUseCMSSearchSelector(s => s.onClickHandlers?.onProjectClicked);
    const id = data.id;
    const [, setToggle] = React.useState<boolean>(true);
    const [confirmDelete, setConfirmDelete] = React.useState<boolean>(false);
    const [deletionRunning, setDeletionRunning] = React.useState<boolean>(false);
    React.useEffect(() => {
        const l = async () => {
            const res = await ControllerHelper.singleCall({ id }, CMSController.GetProjectGroup, true);
            data.loadedData = res;
            setToggle(t => !t);
        }
        if (!data.loadedData)
            l();
    }, [id, data])

    const project = data.loadedData;
    const mayUpdate = hasLink(project, "update");
    const mayDelete = hasLink(project, "delete");
    const mayCopy = mayUpdate;
    const onOpen = React.useCallback(() => {
        if (onProjectClicked)
            onProjectClicked(data.id);
    }, [onProjectClicked, data.id]);
    const onEdit = React.useCallback(() => {
        gotoNamedRoute("cms_project", { id: data.id })
    }, [data.id]);
    const onCopy = React.useCallback(() => {
        gotoNamedRoute("cms_project", { id: data.id, copy: "true" })
    }, [data.id]);
    const onDelete = React.useCallback(() => {
        const del = async () => {
            setDeletionRunning(true);
            const res = await ControllerHelper.singleCall({ id: data.id }, CMSController.DeleteProjectGroup, true);
            if (res || res === undefined) { // DeleteProjectGroup delivers in success case result or undefined! very logical...
                setDeletionRunning(false);  // this should have worked, but it is not, no success response coming
            }
            else
                setDeletionRunning(false);  // this is how it works, although bullshit..

            updateCMSSearchOnRoute(undefined, undefined, undefined, undefined, undefined, undefined, undefined, true);
            setConfirmDelete(false);
        }
        del();

    }, [data.id]);

    const onDummy = React.useCallback(() => {
    }, []);

    const mbe = React.useMemo(() => {
        const toRetEmpty: MoreButtonEntryProps<LoadedDataType<ProjectGroupDownloadDto, ProjectGroupSearch>>[] = [
            {
                onClick: onDummy,
                icon: "exclamation triangle",
                content: t("project is deleted"),
            },
        ];
        const toRet: MoreButtonEntryProps<LoadedDataType<ProjectGroupDownloadDto, ProjectGroupSearch>>[] = [
            {
                onClick: onEdit,
                icon: "edit",
                content: t("edit project"),
                disabled: !mayUpdate
            },
            {
                onClick: onCopy,
                icon: "copy",
                content: t("copy project"),
                disabled: !mayCopy
            },
            {
                onClick: onOpen,
                icon: "folder",
                content: t("open project"),
            },
            {
                onClick: () => setConfirmDelete(t => true),
                icon: "delete",
                content: t("delete project"),
                // disabled: !mayDelete,
                hidden: !mayDelete,
                renderSubComponent: (ele, hide) => (
                    <>
                        <MoreMenuButton icon="times" onClick={() => { hide(); setConfirmDelete(false); }} content={t("cancel deletion")} ele={ele} key={"cancel"} />
                        <MoreMenuButton icon="check" onClick={() => { hide(); onDelete(); }} content={t("yes, delete file")} ele={ele} key={"yes"} />
                    </>
                ),
                showSubComponent: confirmDelete,
            }
        ]
        return !deletionRunning ? toRet : toRetEmpty;
    }, [confirmDelete, mayCopy, mayDelete, mayUpdate, onCopy, onEdit, onOpen, t, onDelete, deletionRunning, onDummy]);

    return <MoreButton
        entries={mbe}
        ele={data}
        kind="transparentButton"
        hideMoreButton
        floated='left'
        onHide={nop}
    />
}

export const getDataFromFileEntrySearchResultDtoLinks = (result: FileEntrySearchResultDto, locationId: string | undefined, lng: string) => {
    if (!result || !result.links || !locationId)
        return undefined;
    const linkKeys = Object.keys(result.links);
    const loc = (locationId ? result.links[locationId] : undefined) ?? (linkKeys.length > 0 ? result.links[linkKeys[0]] : undefined);
    if (loc !== undefined) {
        const locKeys = Object.keys(loc);
        const m = loc[lng] ?? (locKeys.length > 0 ? loc[locKeys[0]] : undefined);
        return m;
    }
    return undefined;
}

export const getMediaFromFileEntrySearchResultDtoLinks = (data: FileEntrySearchResultDto, loc: ContentPostContentLocationDownloadDto | undefined, lng: string) => {
    return getDataFromFileEntrySearchResultDtoLinks(data, loc?.id, lng);
}

export const UseCMSSearchRenderFileVersions = (p: RendererProps<FileEntrySearchResultDto>) => {
    const { data } = p;
    const onFileClicked = useUseCMSSearchSelector(s => s.onClickHandlers?.onFileClicked);
    const locations = useSelector((s: ApplicationState) => s.cmsState.locations);
    const locs = React.useMemo(() => locations ?? [], [locations]);
    const { t } = useImgI18N("cms");
    const links = data.links;
    const { locAndLngs, count } = React.useMemo(() => {
        const locAndLngs: Array<{ location: ContentPostContentLocationDownloadDto, lngs: string[] }> = [];
        let count = 0;
        _.forEach(links, (c, l) => {
            const lngs: string[] = [];
            const location = _.find(locs, ll => ll.id === l);
            if (location) {
                locAndLngs.push({ location, lngs })
                _.forEach(c, (m, lng) => {
                    lngs.push(lng);
                    count++;
                });
            }
        });
        const toRet: { locAndLngs: Array<{ location: ContentPostContentLocationDownloadDto, lngs: string[] }>, count: number } = { locAndLngs, count }
        return toRet;
    }, [links, locs]);

    if (count === 0)
        return <div />;
    return <Button
        kind="fullTransparentButton"
        content={count.toString()}
        onClick={onFileClicked ? () => onFileClicked(data.projectGroupId, data.id) : undefined}
        fluid
        // disabled
        tooltip={{
            tooltipText: "",
            tooltipContent: <CMSFileVersionsInfo locAndLngs={locAndLngs} hideTooltip tooltipHeadline={t("target content locations and languages")} />,
            tooltipContainerMaxWidth: "350px",
            noMargin: true,
            zIndex: 1000000,

        }}
    />
}

export const UseCMSSearchRenderPeview = (p: RendererProps<FileEntrySearchResultDto> & { onPreviewClicked?: (item: FileEntrySearchResultDto) => void }) => {
    const { data, onPreviewClicked } = p;
    const { contentLanguage, location } = useSelector((s: ApplicationState) => ({
        contentLanguage: s.contentLanguage,
        location: s.cmsState.location,
    }), shallowCompare);
    const emptyImageUrl = useThemePart(t => t.emptyImageUrl);
    const onShowPreview = useUseCMSSearchSelector(s => s.onClickHandlers?.onShowPreview);
    const { currentLanguage } = useImgI18N("cms");
    const lng = contentLanguage ?? currentLanguage;
    const click = React.useMemo(() => () => {
        if (onShowPreview)
            onShowPreview(data)
        if (onPreviewClicked)
            onPreviewClicked(data);
    }, [onShowPreview, data, onPreviewClicked])
    const call = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
    }, [])
    const m = getMediaFromFileEntrySearchResultDtoLinks(data, location, lng);
    const thumbs = m?.mediaType?.startsWith("image") ? [m] : m?.thumbnails;
    const pimg = thumbs ? getUriFromLinkByName(thumbs[0]) : undefined;
    const { t } = useImgI18N("cms");
    const hasKoreanFile = React.useMemo(() => {
        if (!data.links || !contentLanguage) return false;
        const firstKey = Object.keys(data.links)[0];
        const hasLanguageKey = contentLanguage in data.links[firstKey];
        const endsWithKoreanPptx = data.links[firstKey][contentLanguage]?.fileName?.endsWith("ko-KR.pptx") || false;
        return hasLanguageKey && endsWithKoreanPptx;
    }, [contentLanguage, data.links]);

    const hideKoreanPreview = contentLanguage === "ko-KR" && hasKoreanFile;

    if (thumbs && thumbs[0]?.mediaType === "text/html") {
        return (
            <div onClick={call}>
                <HDiv onClick={click}>
                    <iframe
                        src={thumbs![0].links[0].uri}
                        title="preview"
                        scrolling="no"
                        style={{
                            width: 198,
                            height: 198,
                            transform: "scale(0.15)",
                            transformOrigin: "top left",
                            border: "none",
                            overflow: "hidden",
                            pointerEvents: "none"
                        }}
                    />
                </HDiv>
            </div>
        );
    } else if ((thumbs && thumbs[0]?.mediaType === "image/tiff") || hideKoreanPreview) {
        return (
            <Tooltip tooltipText={t("no preview supported")} noMargin>
                <Image
                    src={emptyImageUrl}
                    width="40px"
                    squared
                    thumbnail
                    borderColor={"@veryLightGrey"}
                />
            </Tooltip>
        );
    } else {
        return (
            <div onClick={call}>
                <div onClick={click}>
                    <Image
                        src={pimg ?? emptyImageUrl}
                        width="40px"
                        squared
                        thumbnail
                        borderColor={"@veryLightGrey"}
                    />
                </div>
            </div>
        );
    }
}

export const UseCMSSearchRenderProjectImage = (p: RendererProps<ProjectGroupSearch>) => {
    const { data } = p;
    const m = data?.media;
    const thumbs = m?.mediaType?.startsWith("image") ? [m] : m?.thumbnails;
    const pimg = thumbs ? getUriFromLinkByName(thumbs[0]) : undefined;
    const emptyImageUrl = useThemePart(t => t.emptyImageUrl);
    return (
        <div style={{ marginLeft: -8 }}>
            <Image
                src={pimg ?? emptyImageUrl}
                width="40px"
                squared
                thumbnail
                borderColor={"@veryLightGrey"}
                floated="left"
            />
        </div>
    );
}

export const UseCMSSearchFileName = (p: RendererProps<FileEntrySearchResultDto>) => {
    const { data } = p;
    const onFileClicked = useUseCMSSearchSelector(s => s.onClickHandlers?.onFileClicked);
    const { contentLanguage, location } = useSelector((s: ApplicationState) => ({
        contentLanguage: s.contentLanguage,
        location: s.cmsState.location,
    }), shallowCompare);
    const { currentLanguage } = useImgI18N("cms");
    const lng = contentLanguage ?? currentLanguage;
    const m = getMediaFromFileEntrySearchResultDtoLinks(data, location, lng);
    const onShowPreview = useUseCMSSearchSelector(s => s.onClickHandlers?.onShowPreview);
    const fe: FileEntryDownloadDto = {
        contents: {
            a: {
                contents: [{
                    mediaType: m?.mediaType ?? "",
                } as any]
            }
        }
    } as any;
    const click = React.useMemo(() => () => {
        if (onShowPreview)
            onShowPreview(data)
    }, [onShowPreview, data])
    const call = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
    }, [])
    const check = useCheckWhereIAm();
    const isInPublishedProjects = check("cms_content");

    return (
        <div onClick={call}>
            <FG title={m?.fileName ?? ""} onClick={(cmsTheme.openPreviewOnFilenameClick && isInPublishedProjects) ? undefined : (onFileClicked ? () => onFileClicked(data.projectGroupId, data.id) : undefined)}>
                <CMSFileEntryAppIcon fileEntry={fe} />
                <FGC>
                    <FGCCFN onClick={cmsTheme.openPreviewOnFilenameClick && isInPublishedProjects ? click : undefined}>
                        {m?.fileName ?? ""}
                    </FGCCFN>
                </FGC>
            </FG>
        </div>
    );
}

export const UseCMSSearchFileSize = (p: RendererProps<FileEntrySearchResultDto>) => {
    const { data } = p;
    const onFileClicked = useUseCMSSearchSelector(s => s.onClickHandlers?.onFileClicked);
    const { contentLanguage, location } = useSelector((s: ApplicationState) => ({
        contentLanguage: s.contentLanguage,
        location: s.cmsState.location,
    }), shallowCompare);
    const { t, currentLanguage } = useImgI18N("cms");
    const lng = contentLanguage ?? currentLanguage;
    const m = getMediaFromFileEntrySearchResultDtoLinks(data, location, lng);
    const ss = shortenBytes(m?.size ?? 0);
    return (
        <div onClick={(onFileClicked ? () => onFileClicked(data.projectGroupId, data.id) : undefined)}>
            {ss[0]} {t(ss[1])}
        </div>
    );
}

export const UseCMSSearchFileProjectName = (p: RendererProps<FileEntrySearchResultDto>) => {
    const onFileClicked = useUseCMSSearchSelector(s => s.onClickHandlers?.onFileClicked);
    const { data } = p;
    const onProjectClicked = useUseCMSSearchSelector(s => s.onClickHandlers?.onProjectClicked);
    return (
        <FGC title={data.projectGroupName ?? ""} onClick={onProjectClicked ? () => onProjectClicked(data.projectGroupId) : onFileClicked ? () => onFileClicked(data.projectGroupId, data.id) : undefined}>
            {data.projectGroupName ?? ""}
        </FGC>
    );
}

export const UseCMSSearchProjectName = (p: RendererProps<ProjectGroupSearch>) => {
    const theme = React.useContext<Theme>(ThemeContext);
    const { t } = useImgI18N("cms");
    const { data } = p;
    const contentLanguage = useAppSelector(s => s.contentLanguage);
    const name = getTranslated(data.headlines, contentLanguage)?.text ?? ""
    const isPublished = data.isPublished;
    // const isPublished = false;
    const onProjectClicked = useUseCMSSearchSelector(s => s.onClickHandlers?.onProjectClicked);
    return (
        <FF
            title={name}
            onClick={onProjectClicked ? () => onProjectClicked(data.id) : undefined}
            style={{
                color: isPublished ? lightenColor({ theme }, "@accentGreen", 80) : undefined,
                background: isPublished ? fadeColor({ theme }, "@accentGreen", 2) : undefined,
                lineHeight: "28px",
                marginTop: 5
            }}>
            {isPublished &&
                <Tooltip tooltipText={t("project / file(s) published")} noMargin position="top" >
                    <span>
                        <Icon name="published" style={{ float: "left", marginRight: 5, marginLeft: 3, transform: "translateY(3px)" }} color={lightenColor({ theme }, "@accentGreen", 80)} />
                    </span>
                </Tooltip>
            }
            {name}
        </FF>
    );
}

export const UseCMSSearchProjecChanged = (p: RendererProps<FileEntrySearchResultDto>) => {
    const { data } = p;
    const onFileClicked = useUseCMSSearchSelector(s => s.onClickHandlers?.onFileClicked);
    const x = data.changed;
    const changed = x ? getTranslatedStandardDate(x, "Pp") : undefined;

    return (
        <FF title={changed} onClick={onFileClicked ? () => onFileClicked(data.projectGroupId, data.id) : undefined}>
            {changed}
        </FF>
    );
}

export const UseCMSSearchProjecVersion = (p: RendererProps<FileEntrySearchResultDto>) => {
    const { data } = p;
    const onFileClicked = useUseCMSSearchSelector(s => s.onClickHandlers?.onFileClicked);
    const version = String(data.version).padStart(3, " ");

    return (
        <FF onClick={onFileClicked ? () => onFileClicked(data.projectGroupId, data.id) : undefined}>
            {version}
        </FF>
    );
}

export const UseCMSSearchProjectFiles = (p: RendererProps<ProjectGroupSearch>) => {
    const { data } = p;
    const { pfc } = useSelector((s: ApplicationState) => ({ pfc: s.cmsState.projectsFilesCount }), shallowCompare);
    const onProjectClicked = useUseCMSSearchSelector(s => s.onClickHandlers?.onProjectClicked);
    const [files, setFiles] = React.useState<number>(0);

    React.useEffect(() => {
        if (pfc !== undefined && Object.keys(pfc).includes(data.id)) {
            setFiles(pfc[data.id]);
        }
    }, [pfc, data.id]);

    const rounded = (x: number) => {
        if (x >= 1000) {
            return parseFloat((x / 1000).toFixed(1)) + 'k';
        } else {
            return x;
        }
    }

    return (
        <FF onClick={onProjectClicked ? () => onProjectClicked(data.id) : undefined}>
            <>
                {pfc &&
                    <div title={files.toString()}>
                        {rounded(files)}
                    </div>
                }
            </>
        </FF>
    );
}

export const UseCMSSearchProjectId = (p: RendererProps<{ projectId: string, id: string }>) => {
    const { data } = p;
    const onProjectClicked = useUseCMSSearchSelector(s => s.onClickHandlers?.onProjectClicked);
    return (
        <FG onClick={onProjectClicked ? () => onProjectClicked(data.id) : undefined}>
            {data.projectId}
        </FG>
    );
}

export const UseCMSSearchRenderDuration = (p: RendererProps<ProjectGroupSearch>) => {
    const { t } = useImgI18N("cms");
    const tdm = p.data.trainingDurationInMinutes;
    const duration = calcMinsInDaysAndHourAndMins(tdm ?? 0, cmsConfig.trainingDurationHoursperDay);
    const result = <>{t("{{minutes}} mins", { minutes: tdm })}</>;
    const d = duration[0];
    const h = duration[1];
    const m = duration[2];

    if (tdm && (tdm ?? 0) > 0) return (tdm > 45 ?
        <Tooltip tooltipText="" noMargin notInline cursor="default" tooltipContent={
            <LeftAligned>
                <Bolder><Icon name="info" style={{ float: "left", marginRight: 5, marginBottom: 3 }} marginTop={1} /> {t("{{minutes}} mins", { minutes: tdm })}</Bolder>
                {tdm >= cmsConfig.trainingDurationHoursperDay * 60 ?
                    <>
                        {(m === 0 && h > 0 ? t("correspond to {{days}} days and {{hours}} hours", { days: d, hours: h }) :
                            (m > 0 && h === 0 ? t("correspond to {{days}} days and {{mins}} minutes", { days: d, mins: m }) :
                                (m === 0 && h === 0 ? t("correspond to {{days}} days", { days: d }) : t("correspond to {{days}} days, {{hours}} hours and {{mins}} minutes", { days: d, hours: h, mins: m }))))
                        }
                    </> :
                    m > 0 ? t("correspond to {{hours}} hours and {{mins}} minutes", { hours: h, mins: m }) : t("correspond to {{hours}} hours", { hours: h })
                }
            </LeftAligned>
        }>
            {result}
        </Tooltip>
        :
        result
    );
    return <Icon name="times" color="@middleLightGrey" style={{ float: "left" }} />;
}